import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CmsserviceService } from '../../../common-cms-service/cmsservice.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Title, DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-year-four-cal',
    templateUrl: './year-four-cal.component.html',
    styleUrls: ['./year-four-cal.component.css'],
    standalone: false
})
export class YearFourCalComponent implements OnInit, AfterViewInit, OnDestroy {

  // ─── NAV shadow (landing page) ───────────────────────────────────────
  isNavShadow = false;
  private revealObserver!: IntersectionObserver;

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.isNavShadow = window.scrollY > 20;
  }

  // ─── Landing page data ───────────────────────────────────────────────
  // SVG strings are sanitized in ngOnInit via DomSanitizer.bypassSecurityTrustHtml()

  heroPills: { bg: string; label: string; sub: string; svg: SafeHtml }[] = [];
  lpSubjects: { cls: string; title: string; desc: string; svgPath: SafeHtml }[] = [];
  lpFeatures: { title: string; desc: string; svg: SafeHtml }[] = [];
  lpBenefits: { title: string; desc: string; svg: SafeHtml }[] = [];

  private rawHeroPills = [
    {
      bg: 'rgba(0,119,182,.3)', label: 'Maths', sub: 'Problem-solving & arithmetic',
      svg: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>`,
    },
    {
      bg: 'rgba(0,168,232,.3)', label: 'English', sub: 'Comprehension & grammar',
      svg: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>`,
    },
    {
      bg: 'rgba(255,191,0,.3)', label: 'Creative Writing', sub: 'Imagination & expression',
      svg: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>`,
    },
    {
      bg: 'rgba(255,107,107,.3)', label: 'VR & NVR', sub: 'Reasoning & pattern recognition',
      svg: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
    },
  ];

  private rawLpSubjects = [
    {
      cls: 'c1', title: 'Maths', desc: 'Problem-solving skills, arithmetic fluency & logical reasoning to build numerical confidence.',
      svgPath: `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0077b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M17 17h.01M14 14h7v7h-7z"/></svg>`,
    },
    {
      cls: 'c2', title: 'English', desc: 'Comprehension skills, grammar accuracy & vocabulary building for well-rounded language mastery.',
      svgPath: `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#00a8e8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><line x1="8" y1="7" x2="16" y2="7"/><line x1="8" y1="11" x2="14" y2="11"/></svg>`,
    },
    {
      cls: 'c3', title: 'Creative Writing', desc: 'Imagination development, sentence structure & expression enhancement for compelling written work.',
      svgPath: `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ffbf00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>`,
    },
    {
      cls: 'c4', title: 'Verbal Reasoning', desc: 'Early reasoning skills and language-based pattern practice to prepare for 11+ challenges.',
      svgPath: `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ff6b6b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>`,
    },
    {
      cls: 'c5', title: 'Non-Verbal Reasoning', desc: 'Spatial reasoning and visual pattern recognition practice to boost logical thinking.',
      svgPath: `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#7c5cbf" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>`,
    },
  ];

  private rawLpFeatures = [
    {
      title: 'Weekly Tests', desc: 'Structured coverage of topics in manageable weekly segments — never overwhelming, always progressive.',
      svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`,
    },
    {
      title: 'Flexible Schedule', desc: 'Designed to balance school learning and home practice, running October through February at a comfortable pace.',
      svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
    },
    {
      title: 'Consistent Progress', desc: 'Encourages steady academic growth without pressure, backed by progress analysis reports week by week.',
      svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>`,
    },
  ];

  private rawLpBenefits = [
    {
      title: 'Identify Weak Areas', desc: 'Spot improvement areas early so your child can focus where it matters most.',
      svg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1a2b3c" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
    },
    {
      title: 'Progress Analysis Report', desc: 'Track strengths and development over time with detailed weekly performance reports.',
      svg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1a2b3c" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>`,
    },
    {
      title: 'Confidence Boost', desc: 'Step-by-step readiness builds genuine confidence for higher-level preparation.',
      svg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1a2b3c" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`,
    },
    {
      title: 'Complete Syllabus Coverage', desc: 'Maths, English, Creative Writing, VR & NVR — all key areas covered comprehensively.',
      svg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1a2b3c" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>`,
    },
  ];

  lpStats = [
    { number: '5',     suffix: '+',  label: 'Subjects Covered' },
    { number: '20',    suffix: '+',  label: 'Weekly Tests' },
    { number: '100',   suffix: '%',  label: 'Syllabus Coverage' },
    { number: '★ 4.9', suffix: '',   label: 'Parent Rating' },
  ];

  lpSteps = [
    { num: '1', title: 'Sign Up',           desc: 'Quick online registration — takes less than 2 minutes to get started.' },
    { num: '2', title: 'Choose Your Plan',  desc: "Weekly tests from October – February, tailored to your child's schedule." },
    { num: '3', title: 'Start Practising',  desc: 'Immediate access to structured topic tests and progress tracking.' },
  ];

  // ─── Existing calendar component properties ──────────────────────────

  title = 'Year 4 Mock Test';
  slug: any;

  filters: any = { categoryId: 40, pattern: null, mode: null, month: null, center: null, search: '' };

  courses: any[] = [];
  categories: any[] = [];
  patternTypes: any[] = [];
  types: any[] = [];

  months = [
    { label: 'January',   value: 1  }, { label: 'February',  value: 2  }, { label: 'March',     value: 3  },
    { label: 'April',     value: 4  }, { label: 'May',       value: 5  }, { label: 'June',      value: 6  },
    { label: 'July',      value: 7  }, { label: 'August',    value: 8  }, { label: 'September', value: 9  },
    { label: 'October',   value: 10 }, { label: 'November',  value: 11 }, { label: 'December',  value: 12 },
  ];
  centers: string[] = [];

  testList: any[] = [];
  private allResults: any[] = [];

  ngUnsubscribe = new Subject<void>();
  catId = 0;
  catData: any;
  first = true;
  page = 0;
  locationName: any = '';

  private preselectCategoryId: number | null = null;
  private shouldAutoScroll = false;

  aviableCenters = [
    { name: 'Essex',           exams: 'CSSE & CCHS (FSCE)',    locations: 'Chelmsford | Southend | Basildon | Ilford', image: 'assets/images/essex-public-school.jpg' },
    { name: 'Kent',            exams: 'GL Kent & GL Bexley',   locations: 'Bexley Test Centres',                        image: 'assets/images/kent-school.png' },
    { name: 'Redbridge',       exams: 'GL Kent & GL Bexley',   locations: 'Ilford Centre',                              image: 'assets/images/redbridge-school.jpg' },
    { name: 'Buckinghamshire', exams: 'GL Kent & GL Bexley',   locations: 'Amersham | Milton Keynes Centres',           image: 'assets/images/buckshire-school.jpeg' },
    { name: 'Harrow',          exams: 'GL QE Boys, GL HBS Girls, GL Bucks', locations: 'Harrow Centre',               image: 'assets/images/harrow-school.jpg' },
  ];

  constructor(
    private router: ActivatedRoute,
    private titleService: Title,
    private categoryProduct: CmsserviceService,
    private route: Router,
    private ref: ChangeDetectorRef,
    private el: ElementRef,
    private sanitizer: DomSanitizer
  ) {}

  // ─── Lifecycle ───────────────────────────────────────────────────────

  ngOnInit(): void {
    // Sanitize all SVG strings so Angular's [innerHTML] renders them correctly
    this.heroPills   = this.rawHeroPills.map(p => ({ ...p, svg: this.sanitizer.bypassSecurityTrustHtml(p.svg) }));
    this.lpSubjects  = this.rawLpSubjects.map(s => ({ ...s, svgPath: this.sanitizer.bypassSecurityTrustHtml(s.svgPath) }));
    this.lpFeatures  = this.rawLpFeatures.map(f => ({ ...f, svg: this.sanitizer.bypassSecurityTrustHtml(f.svg) }));
    this.lpBenefits  = this.rawLpBenefits.map(b => ({ ...b, svg: this.sanitizer.bypassSecurityTrustHtml(b.svg) }));

    this.router.queryParams.subscribe(params => {
      if (params['category']) {
        const cid = Number(params['category']);
        if (!isNaN(cid)) {
          this.preselectCategoryId = 40;
          this.filters.categoryId = 40;
        }
      }
      if (params['scroll'] === 'calendar-section') {
        this.shouldAutoScroll = true;
      }
    });

    this.router.queryParams.subscribe(params => {
      this.locationName = params.location;
      const ul = $(".test-card-listing-body");
      const li = ul.find(".test-card");
      if (this.locationName && this.testList && this.testList.length) {
        this.locationSpecificList(this.locationName);
      } else if (!this.locationName && li && li.length) {
        this.locationSpecificList('');
      }
    });

    this.loadMasterFiltersAndRender();
    this.setupUiHandlers();
  }

  ngAfterViewInit(): void {
    this.initRevealObserver();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    if (this.revealObserver) {
      this.revealObserver.disconnect();
    }
  }

  // ─── Scroll reveal (landing page) ────────────────────────────────────

  private initRevealObserver(): void {
    const elements = this.el.nativeElement.querySelectorAll('.lp-reveal');
    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            setTimeout(() => entry.target.classList.add('lp-visible'), i * 80);
            this.revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    elements.forEach((el: Element) => this.revealObserver.observe(el));
  }

  // ─── Landing page navigation helpers ─────────────────────────────────

  scrollToSubjects(): void {
    const el = document.getElementById('lp-subjects');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  // ─── Existing calendar methods (all preserved exactly) ───────────────

  private setupUiHandlers() {
    const width = $(window).width();
    if (width < 900) {
      $('.filter-box.filter-tab').eq(0).find('h4').addClass('act');
      $('.filter-box.filter-tab h4').click(function () {
        $('.filter-box h4').removeClass('act');
        $(this).addClass('act');
        $('.filter-box ul').removeClass('act');
        $(this).parent().find('ul').addClass('act');
      });
      $('.open-filter-panel').click(function () { $('#filter-fixed-02').addClass('open'); });
      $('.close-filter-panel').click(function () { $('#filter-fixed-02').removeClass('open'); });
      $('.open-sort-panel').click(function () { $('.sort-panel').addClass('open'); });
      $('.sort-panel .overlay').click(function () { $('.sort-panel').removeClass('open'); });
      $(window).scroll(function () {
        if ($(window).scrollTop() + $(window).height() == $(document).height()) {
          $('.filter-action').hide(); $('.action-btns').hide(); $('.cart-mobile-fixed-btn').hide();
        } else {
          $('.filter-action').show(); $('.action-btns').show(); $('.cart-mobile-fixed-btn').show();
        }
      });
    }

    $('body').on('click', '.tag-ul li', (e: any) => {
      const tagId    = $(e.currentTarget).attr('id');
      const tagClass = $(e.currentTarget).attr('class');
      if (tagClass == 'tag-category') $('[name="category[]"][value="' + tagId + '"]').prop('checked', false);
      if (tagClass == 'tag-course')   $('[name="course[]"][value="' + tagId + '"]').prop('checked', false);
      if (tagClass == 'tag-type')     $('[name="type[]"][value="' + tagId + '"]').prop('checked', false);
      $(e.currentTarget).remove();
      this.applyClientFilters();
    });

    $('body').on('change', '[name="sort"]', (e: any) => {
      const checked = $(e.currentTarget).val();
      $('body').find('[name="release"][value="' + checked + '"]').prop('checked', true);
      this.setFilter();
    });

    $('body').on('keyup', '.filter-search-filter-input', (e: any) => {
      const q = ($(e.currentTarget).val() || '').toString().trim().toUpperCase();
      const ul = $('.test-card-listing-body');
      const li = ul.find('.test-card');
      for (let i = 0; i < li.length; i++) {
        const ftestVal   = li[i].getElementsByClassName('f-test-name')[0].textContent || '';
        const fcatVal    = li[i].getElementsByClassName('f-category-name')[0].textContent || '';
        const fcourseVal = li[i].getElementsByClassName('f-course-name')[0].textContent || '';
        const ftypeVal   = li[i].getElementsByClassName('f-type-name')[0].textContent || '';
        const flocVal    = li[i].getElementsByClassName('f-location-name')[0].textContent || '';
        const ffeeVal    = li[i].getElementsByClassName('f-fees')[0].textContent || '';
        (li[i] as any).style.display = (
          ftestVal.toUpperCase().indexOf(q) > -1 ||
          fcatVal.toUpperCase().indexOf(q)  > -1 ||
          fcourseVal.toUpperCase().indexOf(q) > -1 ||
          ftypeVal.toUpperCase().indexOf(q) > -1 ||
          flocVal.toUpperCase().indexOf(q)  > -1 ||
          ffeeVal.toUpperCase().indexOf(q)  > -1
        ) ? '' : 'none';
      }
    });
  }

  private loadMasterFiltersAndRender() {
    $('.loader-bg').fadeIn();
    this.categoryProduct.getTestFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        const data = (res && res.data) ? res.data : {};
        const allCategories = data.category ? data.category : [];
        const allTypes      = data.type ? data.type : [];
        this.courses        = data.course ? data.course : [];
        this.types          = allTypes;
        this.categories     = allCategories.filter((c: any) => c.category_id === 40);

        const allowedPatternNames = [
          'GL', 'CEM', 'CSSE', 'FSCE', 'SET',
          'GL Redbridge', 'GL Kent', 'GLQE Barnet', 'GL Bucks', 'GL Bexley', 'GL HBS',
        ];
        this.patternTypes = allTypes.filter((t: any) => allowedPatternNames.indexOf(t.type) !== -1);
        this.render();
      }, err => {
        console.error('getTestFilters error', err);
        this.render();
      });
  }

  setFilter()    { this.applyClientFilters(); }
  setFilterRes() { this.setFilter(); }

  resetAllFilters() {
    $('.filter-search-filter-input').val('');
    if ($('form[name="filterBox"]').length)    ($('form[name="filterBox"]')[0] as HTMLFormElement).reset();
    if ($('form[name="filterBoxRes"]').length) ($('form[name="filterBoxRes"]')[0] as HTMLFormElement).reset();
    $('[type="checkbox"]').prop('checked', false);
    this.filters = { categoryId: null, pattern: null, mode: null, month: null, center: null, search: '' };
    this.applyClientFilters();
  }

  render() {
    $('.load-button').css('display', 'none');
    $('.loader-bg').fadeIn();
    const base: any = this.buildBasePayload();
    this.allResults = [];
    const maxPages = 50;
    let pagesFetched = 0;

    const fetchPage = (pageNum: number) => {
      if (pageNum > maxPages) { finalizeLoad(); return; }
      pagesFetched++;
      const formData = { ...base, page: pageNum };
      this.categoryProduct.getTestList(formData)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((res: any) => {
          const chunk = (res && res.data) ? res.data : [];
          if (chunk.length === 0) { finalizeLoad(); return; }
          this.allResults = this.allResults.concat(chunk);
          const extra = res && res.extra && typeof res.extra.extraCount === 'number' ? res.extra.extraCount : null;
          if (extra !== null) {
            extra > 0 ? fetchPage(pageNum + 1) : finalizeLoad();
          } else {
            fetchPage(pageNum + 1);
          }
        }, () => finalizeLoad());
    };

    const finalizeLoad = () => {
      this.centers = this.buildCenters(this.allResults);
      if (this.preselectCategoryId != null) this.filters.categoryId = this.preselectCategoryId;
      this.applyClientFilters();
      if (this.shouldAutoScroll) setTimeout(() => this.scrollToCalendar(), 200);
      $('.loader-bg').fadeOut();
      this.ref.detectChanges();
    };

    fetchPage(1);
  }

  private buildBasePayload() {
    const width = $(window).width();
    let data: any[] = [];
    if (width < 900) {
      $('#filter-fixed-02').removeClass('open');
      if ($('form[name="filterBoxRes"]').length) data = $('form[name="filterBoxRes"]').serializeArray();
    } else {
      if ($('form[name="filterBox"]').length) data = $('form[name="filterBox"]').serializeArray();
    }

    const formData: any = {};
    const course: any[] = []; const category: any[] = []; const type: any[] = [];

    for (let i = 0; i < data.length; i++) {
      if      (data[i]['name'] == 'course[]')   course.push(data[i]['value']);
      else if (data[i]['name'] == 'category[]') category.push(data[i]['value']);
      else if (data[i]['name'] == 'type[]')     type.push(data[i]['value']);
      else formData[data[i]['name']] = data[i]['value'];
    }

    if (this.first && this.catId) category.push(String(this.catId));
    if (!('release'   in formData)) formData['release']   = 'new';
    if (!('exam_type' in formData)) formData['exam_type'] = 'both';
    if (!('search'    in formData)) formData['search']    = '';

    if (this.filters && this.filters.pattern != null) {
      const tid = String(this.filters.pattern);
      if (type.indexOf(tid) === -1) type.push(tid);
    }

    if (this.filters && this.filters.mode) {
      const v = this.filters.mode;
      formData['exam_type'] = (v === 'Online Session') ? 'online'
                            : (v === 'Classroom Session') ? 'classroom'
                            : 'both';
    }

    if (this.filters && this.filters.month  != null) formData['month']    = this.filters.month;
    if (this.filters && this.filters.center)         formData['location'] = this.filters.center;

    formData['course']   = course;
    formData['category'] = category;
    formData['type']     = type;

    const user = +localStorage.getItem('userid');
    formData['user_id'] = (user ? user : 0);
    return formData;
  }

  private isElevenPlus(t: any): boolean {
    if (!t) return false;
    const cid        = Number(t.category_id);
    const courseName = (t.course || '').toString().toLowerCase();
    const courseId   = Number(t.course_id);
    const name       = (t.test_name || '').toString().toLowerCase();
    const ELEVENPLUS_CATEGORY_IDS = [16, 19, 39, 28, 29, 30, 31, 32, 33, 40];
    return (
      courseName === '11plus' || courseId === 1 ||
      ELEVENPLUS_CATEGORY_IDS.indexOf(cid) !== -1 ||
      name.indexOf('11+') !== -1 || name.indexOf('11plus') !== -1
    );
  }

  private monthMap: {[k:string]: number} = { JAN:0,FEB:1,MAR:2,APR:3,MAY:4,JUN:5,JUL:6,AUG:7,SEP:8,OCT:9,NOV:10,DEC:11 };
  private fullMonthMap: {[k:string]: number} = { JANUARY:0,FEBRUARY:1,MARCH:2,APRIL:3,MAY:4,JUNE:5,JULY:6,AUGUST:7,SEPTEMBER:8,OCTOBER:9,NOVEMBER:10,DECEMBER:11 };

  private parseDateString(s?: string): number | null {
    if (!s) return null;
    const str = s.trim();
    let m = str.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (m) { const d=parseInt(m[1],10); const mo=parseInt(m[2],10)-1; let y=parseInt(m[3],10); if(y<100)y+=2000; return Date.UTC(y,mo,d); }
    m = str.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) { const y=parseInt(m[1],10); const mo=parseInt(m[2],10)-1; const d=parseInt(m[3],10); return Date.UTC(y,mo,d); }
    m = str.match(/(?:from\s+)?(?:mon|tue|wed|thu|fri|sat|sun)?\s*([A-Za-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s*(\d{4}))?/i);
    if (m) { const monName=m[1].toUpperCase(); const d=parseInt(m[2],10); const y=m[3]?parseInt(m[3],10):(new Date()).getFullYear(); let monIdx=this.fullMonthMap[monName]; if(monIdx==null)monIdx=this.monthMap[monName.substring(0,3)]; if(monIdx!=null)return Date.UTC(y,monIdx,d); }
    m = str.match(/(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})/);
    if (m) { const d=parseInt(m[1],10); const y=parseInt(m[3],10); const monName=m[2].toUpperCase(); let monIdx=this.fullMonthMap[monName]; if(monIdx==null)monIdx=this.monthMap[monName.substring(0,3)]; if(monIdx!=null)return Date.UTC(y,monIdx,d); }
    return null;
  }

  private getTestTimestamp(t: any): number {
    const td = this.parseDateString((t && t.test_date) || '');
    if (td != null) return td;
    const tn = this.parseDateString((t && t.test_name) || '');
    if (tn != null) return tn;
    return Number.NaN;
  }

  private sortLatestUpcomingFirst(list: any[]): any[] {
    const now = Date.now();
    const future: any[] = []; const past: any[] = []; const unknown: any[] = [];
    for (const item of list) {
      const ts = this.getTestTimestamp(item);
      if (isNaN(ts)) unknown.push(item);
      else if (ts >= now) future.push({ ts, item });
      else past.push({ ts, item });
    }
    future.sort((a,b)=>a.ts-b.ts); past.sort((a,b)=>b.ts-a.ts);
    return future.map(x=>x.item).concat(past.map(x=>x.item)).concat(unknown);
  }

  private applyClientFilters() {
    let filtered = this.allResults.slice().filter(t => this.isElevenPlus(t));

    if (this.filters && this.filters.categoryId != null) {
      const cid = Number(this.filters.categoryId);
      const MOCK_IDS  = [16,28,29,30,31,32,33];
      const TOPIC_IDS = [40];
      const SPRINT_IDS = [39];
      filtered = filtered.filter(t => {
        const tid  = Number(t.category_id);
        const name = ((t.test_name || '')+'').toLowerCase();
        const cat  = ((t.category  || '')+'').toLowerCase();
        if (cid===16)  return MOCK_IDS.includes(tid)   || name.includes('mock')   || cat.includes('mock');
        if (cid===40)  return TOPIC_IDS.includes(tid)  || name.includes('topic')  || cat.includes('topic');
        if (cid===39)  return SPRINT_IDS.includes(tid) || name.includes('sprint') || cat.includes('sprint');
        return tid === cid;
      });
    }

    if (this.filters && this.filters.pattern != null) {
      const patternMeta  = this.patternTypes.find(p => String(p.type_id) === String(this.filters.pattern));
      const patternLabel = patternMeta ? (patternMeta.type||'').toLowerCase() : '';
      if (patternLabel) {
        filtered = filtered.filter(t => {
          const tType = ((t.type||'')+'').toLowerCase();
          const tName = ((t.test_name||'')+'').toLowerCase();
          return tType.includes(patternLabel) || tName.includes(patternLabel);
        });
      }
    }

    if (this.filters && this.filters.mode && this.filters.mode !== 'Both') {
      const modeVal = (this.filters.mode||'').toLowerCase();
      filtered = filtered.filter(t => ((t.exam_type||'')+'').toLowerCase().includes(modeVal));
    }

    if (this.filters && this.filters.month) {
      filtered = filtered.filter(t => this.itemInMonth(t, Number(this.filters.month)));
    }

    if (this.filters && this.filters.center && this.filters.center !== 'Any') {
      const centerName = (this.filters.center||'').toLowerCase();
      filtered = filtered.filter(t => ((t.all_location||t.location||'')+'').toLowerCase().includes(centerName));
    }

    if (this.filters && this.filters.search) {
      const q = (this.filters.search+'').toLowerCase().trim();
      if (q) {
        filtered = filtered.filter(t => {
          const name   = ((t.test_name||'')+'').toLowerCase();
          const loc    = ((t.all_location||t.location||'')+'').toLowerCase();
          const course = ((t.course||'')+'').toLowerCase();
          const cat    = ((t.category||'')+'').toLowerCase();
          const type   = ((t.type||'')+'').toLowerCase();
          return name.includes(q)||loc.includes(q)||course.includes(q)||cat.includes(q)||type.includes(q);
        });
      }
    }

    this.testList = this.sortLatestUpcomingFirst(filtered);
    this.centers  = this.buildCenters(this.allResults);
    $('.load-button').css('display', 'none');
    $('.loader-bg').fadeOut();
    this.ref.detectChanges();
  }

  loadMore(append: boolean) { this.applyClientFilters(); }

  private itemMatchesPattern(t: any, tid: string): boolean {
    const meta  = this.patternTypes.find(x => String(x.type_id) === tid);
    if (!meta) return true;
    const label = (meta.type||'').toString().toUpperCase();
    const name  = ((t && t.test_name)||'').toString().toUpperCase();
    const ALIASES: { [k: string]: RegExp[] } = {
      'GL': [/\bGL\b/,/\bGL[-_]/,/\bGL[A-Z]{2}\d*/], 'CEM': [/\bCEM\b/],
      'CSSE': [/\bCSSE\b/], 'FSCE': [/\bFSCE\b/], 'SET': [/\bSET\b/],
      'GL REDBRIDGE': [/\bREDBRIDGE\b/,/\bGLRB\d*\b/], 'GL BEXLEY': [/\bBEXLEY\b/,/\bGLBX\d*\b/],
      'GL KENT': [/\bKENT\b/,/\bGLKE\d*\b/], 'GL BUCKS': [/\bBUCKS\b/,/\bGLBU\d*\b/],
      'GL QE BARNET': [/\bQE\b/,/\bQE\s*BARNET\b/,/\bGLQE\d*\b/], 'GL HBS': [/\bHBS\b/,/\bGLHBS\d*\b/],
    };
    const patterns = ALIASES[label];
    if (patterns && patterns.length) return patterns.some(rx => rx.test(name));
    return name.indexOf(label) !== -1 || name.replace(/\s+/g,'').indexOf(label.replace(/\s+/g,'')) !== -1;
  }

  private itemInMonth(t: any, monthNum: number): boolean {
    const s = ((t && t.test_date)||'').toString().toUpperCase();
    if (!s) return true;
    const monthsShort = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    const monthsLong  = ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'];
    const idx = monthNum - 1;
    const mm  = (monthNum < 10 ? '0' + monthNum : '' + monthNum);
    return (
      s.indexOf(monthsShort[idx]) !== -1 || s.indexOf(monthsLong[idx]) !== -1 ||
      new RegExp('\\b\\d{4}-' + mm + '-\\d{1,2}\\b').test(s) ||
      new RegExp('\\b\\d{1,2}/' + mm + '/\\d{2,4}\\b').test(s)
    );
  }

  private buildCenters(list: any[]): string[] {
    const set: any = {};
    for (let i = 0; i < (list||[]).length; i++) {
      const v = ((list[i].all_location || list[i].location || '')+'').trim();
      if (v) set[v] = true;
    }
    return Object.keys(set).sort();
  }

  trimChar(str: string, length = 30) {
    if (!str) return '';
    return str.length < length ? str : str.substring(0, length) + '...';
  }

  isRangeDate(dateStr: string): boolean {
    if (!dateStr) return false;
    return /^(from\s+)/i.test(dateStr.trim());
  }

  private locationSpecificList(val?: any): void {
    let filter = (val || '').toUpperCase();
    const ul = $('.test-card-listing-body');
    const li = ul.find('.test-card');
    for (let i = 0; i < li.length; i++) {
      const ftestVal   = li[i].getElementsByClassName('f-test-name')[0].textContent || '';
      const fcatVal    = li[i].getElementsByClassName('f-category-name')[0].textContent || '';
      const fcourseVal = li[i].getElementsByClassName('f-course-name')[0].textContent || '';
      const ftypeVal   = li[i].getElementsByClassName('f-type-name')[0].textContent || '';
      const flocVal    = li[i].getElementsByClassName('f-location-name')[0].textContent || '';
      const ffeeVal    = li[i].getElementsByClassName('f-fees')[0].textContent || '';
      (li[i] as any).style.display = (
        ftestVal.toUpperCase().indexOf(filter)   > -1 ||
        fcatVal.toUpperCase().indexOf(filter)    > -1 ||
        fcourseVal.toUpperCase().indexOf(filter) > -1 ||
        ftypeVal.toUpperCase().indexOf(filter)   > -1 ||
        flocVal.toUpperCase().indexOf(filter)    > -1 ||
        ffeeVal.toUpperCase().indexOf(filter)    > -1
      ) ? '' : 'none';
    }
  }

  scrollToCalendar() {
    const calendarElement = document.getElementById('calendar-section');
    if (calendarElement) calendarElement.scrollIntoView({ behavior: 'smooth' });
  }

  scrrollToOffer() {
    const offerElement = document.getElementById('offer-section');
    if (offerElement) offerElement.scrollIntoView({ behavior: 'smooth' });
  }

  downloadCalendar() {
    window.location.href = 'https://drive.google.com/uc?export=download&id=1iOO5PXJtTqgihbsgo1lRVciN7xufo4kO';
  }

  downloadsprint() {
    window.location.href = 'https://drive.google.com/uc?export=download&id=1at2OesLa2qU5T6HPkJIm0bvhbnSgpNZv';
  }
}